// CreateMeetingModal.js
import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Row, Col, Spinner, Alert } from "react-bootstrap";
import Select from "react-select";
import Multiselect from "multiselect-react-dropdown";
import CreatableSelect from "react-select/creatable";
import { createLiveClass } from "ServerAPI/serverAPI";
import { Calendar, Clock, FileText, Tag, Hash } from "react-bootstrap-icons";
import { Users } from "lucide-react";

const CreateMeetingModal = ({
    show,
    onHide,
    userGroupId,
    existingUsers,
    cookies,
    showToast,
    onMeetingCreated,
}) => {
    const getInitialState = () => ({
        title: "",
        date: "",
        startTime: "",
        endTime: "",
        participants: [],
        organizer: "",
        description: "", // opt
        maxParticipants: "", // opt
        timezone: "IST", // DEFAULT TO IST
        tags: [], // opt
        // status: "scheduled", // opt WIP
        // isRecurring: false, // opt WIP
        // recurringPattern: "weekly", // opt
    });

    const [meetingData, setMeetingData] = useState(getInitialState());
    const [validated, setValidated] = useState(false);
    const [isCreatingMeeting, setIsCreatingMeeting] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    // Reset form when modal closes or opens
    useEffect(() => {
        if (show) {
            setMeetingData(getInitialState());
            setValidated(false);
            setFormErrors({});
        }
    }, [show]);

    // Set minimum date to today
    const today = new Date().toISOString().split("T")[0];

    // Format date from YYYY-MM-DD to DD-MM-YYYY for the payload
    const formatDateForAPI = (dateString) => {
        if (!dateString) return "";
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setMeetingData({ ...meetingData, [name]: value });

        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const handleParticipantsChange = (selectedOptions) => {
        const participantIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
        setMeetingData(prev => ({ ...prev, participants: participantIds }));

        if (formErrors.participants) {
            setFormErrors(prev => ({ ...prev, participants: "" }));
        }
    };

    const handleTagsChange = (newValue) => {
        setMeetingData(prev => ({ ...prev, tags: newValue || [] }));
    };

    const validateForm = () => {
        const errors = {};
        if (!meetingData.title.trim()) errors.title = "Meeting title is required";
        if (!meetingData.date) errors.date = "Date is required";
        else if (new Date(meetingData.date) < new Date(today)) errors.date = "Date cannot be in the past";
        if (!meetingData.startTime) errors.startTime = "Start time is required";
        if (!meetingData.endTime) errors.endTime = "End time is required";
        if (meetingData.startTime && meetingData.endTime && meetingData.endTime <= meetingData.startTime) {
            errors.endTime = "End time must be after start time";
        }
        if (meetingData.participants.length === 0) errors.participants = "At least one participant is required";

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateMeeting = async (e) => {
        e.preventDefault();
        setValidated(true);

        if (!validateForm()) {
            showToast("Please fix the form errors", "error");
            return;
        }

        setIsCreatingMeeting(true);

        try {
            // Base payload with required fields
            const payload = {
                userGroupId: userGroupId,
                title: meetingData.title,
                organizer: cookies.userId,
                date: meetingData.date,
                startTime: meetingData.startTime,
                endTime: meetingData.endTime,
                participants: meetingData.participants,
                timezone: meetingData.timezone,
            };

            // Add optional fields if they have a value
            if (meetingData.description) payload.description = meetingData.description;
            if (meetingData.maxParticipants) payload.maxParticipants = Number(meetingData.maxParticipants);
            if (meetingData.tags.length > 0) payload.tags = meetingData.tags.map(tag => tag.value);

            // Add WIP fields (currently commented out)
            // payload.status = meetingData.status;
            // payload.isRecurring = meetingData.isRecurring;
            // if (meetingData.isRecurring) {
            //     payload.recurringPattern = meetingData.recurringPattern;
            // }

            const response = await createLiveClass("", payload);

            if (response.status === 201) {
                onMeetingCreated();
                showToast("Live class created successfully", "success");
                onHide();
            } else {
                showToast("Failed to create live class", "error");
            }
        } catch (error) {
            console.error("Error creating meeting:", error);
            showToast(error?.response?.data?.message || "An error occurred", "error");
        } finally {
            setIsCreatingMeeting(false);
        }
    };

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            fontSize: "14px",
            padding: "4px 8px",
            borderRadius: "8px",
            borderColor: state.isInvalid ? "#dc3545" : (state.isFocused ? "#224D99" : "#ced4da"),
            boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(34, 77, 153, 0.25)" : "none",
            "&:hover": { borderColor: state.isInvalid ? "#dc3545" : "#224D99" }
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: "#224D9920",
            borderRadius: "6px"
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: "#224D99",
            fontSize: "13px"
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: "#224D99",
            "&:hover": { backgroundColor: "#224D99", color: "white" }
        })
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title style={{ color: "#224D99", fontSize: "24px", fontWeight: 600 }}>
                    🎥 Schedule Live Class
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="pt-2">
                <Form noValidate validated={validated} onSubmit={handleCreateMeeting}>
                    {/* Title */}
                    <Row className="mb-3">
                        <Col>
                            <Form.Group>
                                <Form.Label className="d-flex align-items-center mb-2">
                                    <FileText size={16} className="me-2 text-primary" /><strong>Class Title</strong>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g., Advanced Algebra, Weekly Q&A"
                                    name="title"
                                    value={meetingData.title}
                                    onChange={handleChange}
                                    isInvalid={!!formErrors.title}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.title}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Description */}
                    <Row className="mb-3">
                        <Col>
                            <Form.Group>
                                <Form.Label className="d-flex align-items-center mb-2">
                                    <FileText size={16} className="me-2 text-muted" /><strong>Description</strong> (Optional)
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Provide a brief summary of the class, topics covered, and any prerequisites."
                                    name="description"
                                    value={meetingData.description}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Date and Time */}
                    <Row className="mb-3">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="d-flex align-items-center mb-2">
                                    <Calendar size={16} className="me-2 text-success" /><strong>Date</strong>
                                </Form.Label>
                                <Form.Control type="date" name="date" value={meetingData.date} onChange={handleChange} min={today} isInvalid={!!formErrors.date} required />
                                <Form.Control.Feedback type="invalid">{formErrors.date}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="d-flex align-items-center mb-2">
                                    <Clock size={16} className="me-2 text-warning" /><strong>Start Time</strong>
                                </Form.Label>
                                <Form.Control type="time" name="startTime" value={meetingData.startTime} onChange={handleChange} isInvalid={!!formErrors.startTime} required />
                                <Form.Control.Feedback type="invalid">{formErrors.startTime}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="d-flex align-items-center mb-2">
                                    <Clock size={16} className="me-2 text-danger" /><strong>End Time</strong>
                                </Form.Label>
                                <Form.Control type="time" name="endTime" value={meetingData.endTime} onChange={handleChange} isInvalid={!!formErrors.endTime} required />
                                <Form.Control.Feedback type="invalid">{formErrors.endTime}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Form.Text className="text-muted mt-2 ps-3">Timezone is fixed to India Standard Time (IST).</Form.Text>
                    </Row>

                    {/* Participants and Capacity */}
                    <Row className="mb-3">
                        <Col>
                            <Form.Group>
                                <Form.Label className="d-flex align-items-center mb-2">
                                    <Users size={16} className="me-2 text-info" /><strong>Participants</strong>
                                    <span className="ms-2 text-muted" style={{ fontSize: "12px" }}>({meetingData.participants.length} selected)</span>
                                </Form.Label>
<Multiselect
  options={existingUsers}
  selectedValues={existingUsers.filter(user => meetingData.participants.includes(user.value))}
  onSelect={(selectedList) => {
    const participants = selectedList.map(user => user.value);
    setMeetingData(prev => ({ ...prev, participants }));
  }}
  onRemove={(selectedList) => {
    const participants = selectedList.map(user => user.value);
    setMeetingData(prev => ({ ...prev, participants }));
  }}
  displayValue="label"
  placeholder="Search and select participants..."
  closeOnSelect={false}
  showCheckbox={true}

      style={{
          option: {
            backgroundColor: '#f5f7fa', // very light gray blue
            color: '#2c3e50',           // dark slate blue
          },
          selectedOption: {
            backgroundColor: '#7fb3d5', // soft blue
            color: 'white',
          },
          chips: {
            backgroundColor: '#85cbfdff', // light blue-gray
            color: 'black',           // medium blue text
            fontWeight: '500'
          },
          searchBox: {
            border: '1px solid #ccd6e0',
            borderRadius: '6px',
            padding: '8px',
          },
          optionContainer: {
            maxHeight: '180px',
            overflowY: 'auto',
          },
        }}
        className={formErrors.participants ? "multiselect-invalid" : ""}
      />
                                {formErrors.participants && <div className="d-block invalid-feedback">{formErrors.participants}</div>}
                            </Form.Group>
                        </Col>
                        {/* <Col md={8}>
                            <Form.Group>
                                <Form.Label className="d-flex align-items-center mb-2">
                                    <Users size={16} className="me-2 text-info" /><strong>Participants</strong>
                                    <span className="ms-2 text-muted" style={{ fontSize: "12px" }}>({meetingData.participants.length} selected)</span>
                                </Form.Label>
                                <Select
                                    options={existingUsers}
                                    isMulti
                                    value={existingUsers.filter(user => meetingData.participants.includes(user.value))}
                                    onChange={handleParticipantsChange}
                                    placeholder="Search and select participants..."
                                    closeMenuOnSelect={false}
                                    styles={selectStyles}
                                    isInvalid={!!formErrors.participants}
                                />
                                {formErrors.participants && <div className="d-block invalid-feedback">{formErrors.participants}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="d-flex align-items-center mb-2">
                                    <Hash size={16} className="me-2 text-muted" /><strong>Max Participants</strong> (Opt)
                                </Form.Label>
                                <Form.Control type="number" placeholder="e.g., 50" name="maxParticipants" value={meetingData.maxParticipants} onChange={handleChange} min="1" />
                            </Form.Group>
                        </Col> */}
                    </Row>

                    {/* Tags */}
                    {/* <Row className="mb-4">
                        <Col>
                            <Form.Group>
                                <Form.Label className="d-flex align-items-center mb-2">
                                    <Tag size={16} className="me-2 text-secondary" /><strong>Tags</strong> (Optional)
                                </Form.Label>
                                <CreatableSelect
                                    isMulti
                                    onChange={handleTagsChange}
                                    value={meetingData.tags}
                                    placeholder="Type to create tags like 'math', 'algebra'..."
                                    styles={selectStyles}
                                />
                                <Form.Text className="text-muted">Tags help in categorizing and finding classes easily.</Form.Text>
                            </Form.Group>
                        </Col>
                    </Row> */}

                    {/* WIP: Recurring Options - Add UI elements here when ready */}
                    {/* <Row className="mb-3">
                        <Col>
                            <Form.Group>
                                <Form.Check 
                                    type="switch"
                                    id="is-recurring-switch"
                                    label="This is a recurring class"
                                    name="isRecurring"
                                    checked={meetingData.isRecurring}
                                    onChange={(e) => setMeetingData({...meetingData, isRecurring: e.target.checked})}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    */}
                </Form>
            </Modal.Body>

            <Modal.Footer className="border-0 pt-0">
                <Button variant="secondary" onClick={onHide} disabled={isCreatingMeeting}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleCreateMeeting} disabled={isCreatingMeeting} style={{ minWidth: '130px', backgroundColor: "#224D99", borderColor: "#224D99" }}>
                    {isCreatingMeeting ? (
                        <><Spinner as="span" animation="border" size="sm" role="status" className="me-2" />Creating...</>
                    ) : (
                        "🎥 Create Class"
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateMeetingModal;